import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import {
  BlockCollapseToggle,
  BlockHeader,
  FORM_GROUP_TITLE_SX,
  LotUserPrivateInfoSection,
  LotUserPrivateInfoSkeleton,
  ORGANIZE_BLOCK_SX,
  PRIVATE_BLOCK_SX,
  PUBLICATION_BLOCK_SX,
  SECTION_BAR_ICON_BUTTON_SX,
} from './formBlockUi';
import { toast } from '../../../../Components/Toast';
import { getLotUserContent } from '../../../../services/lotUserContentService';
import {
  LOT_CONTENT_OPERATION_LABEL,
  buildLotUserContentCommercialSummary,
  hasLotUserContentCommercialData,
  mapLotUserContentFromApi,
} from '../../../utils/lotUserContentFormUtils';
import { normalizeListingVideosFromPayload } from '../../../utils/listingVideoUtils';
import LotContentImages from './LotContentImages';
import LotContentVideos from './LotContentVideos';
import LotContentAttachments from './LotContentAttachments';
import { normalizeListingAttachmentsFromPayload } from '../../../utils/listingAttachmentUtils';
import { PRIVATE_BLOCK_TITLE_DEFAULT } from '../../../utils/spaceMirrorLots';

const FIELD_LABEL_SX = {
  fontSize: '11px',
  color: 'rgba(255,255,255,0.45)',
  mb: 0.5,
};

const FIELD_VALUE_SX = {
  fontSize: '13px',
  color: 'rgba(255,255,255,0.88)',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const EMPTY_VALUE_SX = {
  ...FIELD_VALUE_SX,
  color: 'rgba(255,255,255,0.35)',
  fontStyle: 'italic',
};

const ReadOnlyField = ({ label, value }) => {
  const hasValue = Boolean(String(value ?? '').trim());

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
        <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      </Box>
      <Typography sx={hasValue ? FIELD_VALUE_SX : EMPTY_VALUE_SX}>
        {hasValue ? value : '—'}
      </Typography>
    </Box>
  );
};

const LotUserContentView = ({
  lotId,
  userLotLists = [],
  onBack,
  onEdit,
  embedded = false,
  ownerUserId = null,
  privateBlockTitle = PRIVATE_BLOCK_TITLE_DEFAULT,
  /** Embebida en Lote: acción del header de "Publicación" (p. ej. Editar información privada). */
  publicationAction = null,
  /** Nodo debajo de «Datos comerciales» (p. ej. «Precio vs. mercado»). */
  afterCommercial = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [privateExpanded, setPrivateExpanded] = useState(false);
  const [organizeExpanded, setOrganizeExpanded] = useState(false);

  useEffect(() => {
    if (!lotId) return undefined;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await getLotUserContent(lotId, { ownerUserId });
        if (cancelled) return;
        if (!res._httpOk) {
          toast.error({ title: 'Error', message: 'No se pudo cargar la información del lote.' });
          return;
        }
        const data = res.data || {};
        const mapped = mapLotUserContentFromApi(data);
        setForm(mapped);
        setImages(Array.isArray(data.images) ? data.images : []);
        setVideos(normalizeListingVideosFromPayload(data));
        setAttachments(normalizeListingAttachmentsFromPayload(data));
        // Vista: el bloque privado arranca cerrado (en el formulario de edición se abre si hay datos).
        setPrivateExpanded(false);
        setOrganizeExpanded(Array.isArray(mapped.list_ids) && mapped.list_ids.length > 0);
      } catch (e) {
        if (!cancelled) {
          console.error('Error loading lot content view:', e);
          toast.error({ title: 'Error', message: 'No se pudo cargar la información del lote.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [lotId, ownerUserId]);

  const listNames = (Array.isArray(form?.list_ids) ? form.list_ids : [])
    .map((id) => userLotLists.find((row) => row.id === id)?.name)
    .filter(Boolean);

  const commercialSummary = form && hasLotUserContentCommercialData(form)
    ? buildLotUserContentCommercialSummary(form)
    : null;

  const operationLabel = form?.operation
    ? (LOT_CONTENT_OPERATION_LABEL[form.operation] || form.operation)
    : null;

  const contentBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, flex: 1, overflowY: 'auto' }}>
      <Box sx={{ ...PRIVATE_BLOCK_SX, p: 1.5 }}>
        <BlockHeader
          icon={<LockOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)', mt: 0.15 }} />}
          title={privateBlockTitle}
          subtitle="Campos privados que no se comparten con visitantes."
          action={(
            <BlockCollapseToggle
              expanded={privateExpanded}
              onToggle={() => setPrivateExpanded((v) => !v)}
              labelExpanded="Contraer sección privada"
              labelCollapsed="Expandir sección privada"
            />
          )}
        />
        <Collapse in={privateExpanded}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.25 }}>
            <ReadOnlyField label="Etiqueta interna" value={form?.unit_label} />
            <ReadOnlyField label="Notas privadas" value={form?.observations} />
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <BlockHeader
          icon={<PublicOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', mt: 0.15 }} />}
          title="Publicación"
          subtitle={ownerUserId ? undefined : 'Contenido descriptivo del lote.'}
          action={publicationAction}
        />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 0 }}
            >
              Presentación
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <ReadOnlyField label="Título" value={form?.title} />
            <ReadOnlyField label="Descripción" value={form?.description} />
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 0 }}
            >
              Datos comerciales
            </Typography>
          </Box>
          <Typography sx={commercialSummary ? FIELD_VALUE_SX : EMPTY_VALUE_SX}>
            {commercialSummary || '—'}
          </Typography>
          {operationLabel && !commercialSummary && (
            <Typography sx={{ ...FIELD_VALUE_SX, mt: 0.5 }}>
              {operationLabel}
            </Typography>
          )}
        </Box>

        {afterCommercial}

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.25 }}>
          <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1 }}>Fotos</Typography>
          {images.length > 0 ? (
            <LotContentImages
              lotId={lotId}
              canEdit={false}
              initialImages={images}
            />
          ) : (
            <Typography sx={EMPTY_VALUE_SX}>Sin fotos cargadas</Typography>
          )}
        </Box>

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.25 }}>
          <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1 }}>Videos</Typography>
          {videos.length > 0 ? (
            <LotContentVideos
              lotId={lotId}
              canEdit={false}
              initialVideos={videos}
              ownerUserId={ownerUserId}
            />
          ) : (
            <Typography sx={EMPTY_VALUE_SX}>Sin videos cargados</Typography>
          )}
        </Box>

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.25 }}>
          <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1 }}>Adjuntos</Typography>
          {attachments.length > 0 ? (
            <LotContentAttachments
              lotId={lotId}
              canEdit={false}
              initialAttachments={attachments}
              ownerUserId={ownerUserId}
            />
          ) : (
            <Typography sx={EMPTY_VALUE_SX}>Sin adjuntos cargados</Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ ...ORGANIZE_BLOCK_SX, p: 1.5 }}>
        <BlockHeader
          icon={<FolderOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />}
          title="Listas"
          action={(
            <BlockCollapseToggle
              expanded={organizeExpanded}
              onToggle={() => setOrganizeExpanded((v) => !v)}
              labelExpanded="Contraer listas"
              labelCollapsed="Expandir listas"
            />
          )}
        />
        <Collapse in={organizeExpanded}>
          <Box sx={{ mt: 1.25 }}>
            {listNames.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {listNames.map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    label={name}
                    sx={{
                      height: 26,
                      fontSize: '12px',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      color: 'rgba(229,231,235,0.95)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={EMPTY_VALUE_SX}>Sin listas asignadas</Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );

  if (embedded) {
    return loading ? <LotUserPrivateInfoSkeleton /> : contentBody;
  }

  const privateInfoActions = (
    <>
      <Tooltip title="Editar" placement="top" arrow>
        <IconButton
          size="small"
          onClick={onEdit}
          aria-label="Editar información privada del lote"
          sx={SECTION_BAR_ICON_BUTTON_SX}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Volver" placement="top" arrow>
        <IconButton
          size="small"
          onClick={onBack}
          aria-label="Volver al panel del lote"
          sx={SECTION_BAR_ICON_BUTTON_SX}
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, minHeight: 0 }}>
      <LotUserPrivateInfoSection loading={loading} actions={privateInfoActions}>
        {contentBody}
      </LotUserPrivateInfoSection>
    </Box>
  );
};

export default LotUserContentView;
